import React, {useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, Typography} from "@mui/material";
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import LightModeIcon from '@mui/icons-material/LightMode';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import BedIcon from '@mui/icons-material/Bed';
import {useNavigate} from "react-router-dom";
import Bottom from "../Bottom";
import Top from "../Top";
import GaugeChart from "../../components/GaugeChart";
import LineChart from "../../components/LineChart";
import DeviceNormalRange from "../../components/DeviceNormalRange";
import bloodIcon from "../../assets/icon/blood.gif";
import * as util from "../../utils/util";
import {BLOOD_TIME_SLOTS, DeviceInfo} from "../../utils/deviceInfo";
import {bloodLatestOf, bloodTrendOf, useHealthDevice} from "../../utils/healthDeviceApi";

const TREND_DAY_COUNT = 7;

// 아침 / 점심 / 저녁 식전·식후 한 줄
// const MEAL_ROWS = [
//   { beforeKey: 'bloodSugarMorning', afterKey: 'bloodSugarMorningAfter', label: '아침', icon: <WbTwilightIcon sx={{ color: '#ef8322', mx: 1.5 }} /> },
//   { beforeKey: 'bloodSugarLunch', afterKey: 'bloodSugarLunchAfter', label: '점심', icon: <LightModeIcon sx={{ color: '#fd5c5c', mx: 1.5 }} /> },
//   { beforeKey: 'bloodSugarDinner', afterKey: 'bloodSugarDinnerAfter', label: '저녁', icon: <BedtimeIcon sx={{ color: '#ffb70d', mx: 1.5 }} /> },
// ];

//2026-08-11 수정
const MEAL_ROWS = [
  {
    beforeKey: 'breakfast_pre',
    afterKey: 'breakfast_post',
    label: '아침',
    icon: <WbTwilightIcon sx={{ color: '#ef8322', mx: 1.5 }} />
  },
  {
    beforeKey: 'lunch_pre',
    afterKey: 'lunch_post',
    label: '점심',
    icon: <LightModeIcon sx={{ color: '#fd5c5c', mx: 1.5 }} />
  },
  {
    beforeKey: 'dinner_pre',
    afterKey: 'dinner_post',
    label: '저녁',
    icon: <BedtimeIcon sx={{ color: '#ffb70d', mx: 1.5 }} />
  },
];

const HistoryDeviceBloodDetail = (props: { device: DeviceInfo }) => {
  const device = props.device;
  const navigate = useNavigate();
  const [dayValue, setDayValue] = useState(0);

  const targetDate = util.getDateByCustom('yyyyMMdd', dayValue);
  // AP_009_004 — meal_tag(breakfast_pre …)가 화면의 시간대 키와 같아 그대로 쓴다
  const { data: deviceData } = useHealthDevice(dayValue);
  const targetBlood: any = bloodLatestOf(deviceData, dayValue);
  const bloodTrend = bloodTrendOf(deviceData, TREND_DAY_COUNT, dayValue);

  const latest = util.getLatestValue(targetBlood);
  const latestStatus = util.getPressureStatusAndColor(latest.type, latest.bloodValue);

  // 최근 7일 (오늘 포함) 날짜 라벨
  const trendLabels: string[] = [];
  for (let index = 0; index < TREND_DAY_COUNT; index++) {
    const date = util.getDateByCustom('yyyyMMdd', dayValue + index - (TREND_DAY_COUNT - 1));
    trendLabels.push(date.slice(4, 6) + '.' + date.slice(6, 8));
  }

  const series = BLOOD_TIME_SLOTS.map((slot) => ({ curve: 'linear', data: bloodTrend[slot.key], color: slot.color }));
  const xAxis = [{ scaleType: 'point', data: trendLabels }];

  return (
    <Stack sx={{ width: '100%', minWidth: 320, background: '#F1F2F4', pb: 12, pl: 2, pr: 2, boxSizing: 'border-box' }} spacing={6}>
      <Top title={device.typeName + ' 기록'} />

      <Grid sx={{ bgcolor: '#F1F2F4', p: 0, width: '100%', minHeight: 500 }}>
        <Box>

          <Typography component="div" sx={{ fontSize: 20, color: '#555', m: 4, mb: 0, textAlign: 'center', fontWeight: 700 }}>
            <ArrowLeftIcon onClick={() => setDayValue((prev: any) => prev - 1)} sx={{ verticalAlign: 'text-bottom' }} />
            {targetDate.slice(0, 4) + '.' + targetDate.slice(4, 6) + '.' + targetDate.slice(6, 8)}
            <ArrowRightIcon onClick={() => setDayValue((prev: any) => prev + 1)} sx={{ 'display': dayValue < 0 ? 'inline-block' : 'none', verticalAlign: 'text-bottom' }} />
          </Typography>

          {/* 요약 */}
          <Box sx={{ m: '20px 0', bgcolor: '#fff', borderRadius: '15px', boxShadow: CardShadow }}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ p: '10px 15px 8px 15px' }}>
              <Typography sx={{ fontSize: 14, color: '#333', fontWeight: 700 }}>{device.name}</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: device.excluded ? '#bbb' : '#59b544', border: '1px solid', borderColor: device.excluded ? '#e0e0e0' : '#a5dd97', borderRadius: '10px', p: '2px 8px' }}>
                {device.excluded ? '실증 제외' : '연동됨'}
              </Typography>
            </Stack>
            <Divider />

            <Stack direction={'row'} justifyContent={'center'} sx={{ p: 1 }}>
              <Grid>
                <GaugeChart targetValue={latestStatus.gaugeValue} />
                <Typography component={'div'} sx={{ width: '100%', textAlign: 'center', color: latestStatus.color, fontSize: 16, fontWeight: 700, mb: 1 }}>
                  {latestStatus.status}
                </Typography>
              </Grid>

              <Stack justifyContent={'center'} sx={{ m: 'auto' }}>
                <Avatar alt="" src={bloodIcon} sx={{ m: 'auto', width: '60px', height: '60px', borderRadius: 0 }} />
              </Stack>

              <Stack direction={'row'} alignItems={'center'} sx={{ ml: 'auto', mr: 2 }}>
                <Stack direction={'column'} sx={{ ml: 'auto' }}>
                  <Typography component="div" sx={{ fontSize: 30, color: latestStatus.color, fontWeight: 700, textAlign: 'center' }}>
                    {latest.bloodValue}
                  </Typography>
                  <Typography component="span" sx={{ fontSize: 18, color: '#777', mb: 0, textAlign: 'center' }}>
                    mg/dL
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Box>

          {/* 시간대별 혈당 기록 */}
          <Accordion defaultExpanded sx={{ borderRadius: '15px', position: 'static', mb: '20px', '&:before': { display: 'none' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="device-blood-content"
              id="device-blood-header"
              sx={{
                '&#device-blood-header': { minHeight: '55px' },
                '& .MuiAccordionSummary-content.Mui-expanded': { m: 0 }
              }}
            >
              <Typography sx={{ fontSize: 16, color: '#555', fontWeight: 700 }}>혈당 기록</Typography>
            </AccordionSummary>
            <Divider />

            <AccordionDetails>
              <Stack direction={'row'} justifyContent={'space-evenly'} alignItems={'center'} sx={{ bgcolor: '#eee', borderRadius: 3 }}>
                <Typography sx={{ p: 1, fontSize: 14, fontWeight: 700, color: '#555' }}>식전</Typography>
                <Divider orientation="vertical" variant="middle" flexItem />
                <Typography sx={{ p: 1, fontSize: 14, fontWeight: 700, color: '#555' }}>식후</Typography>
              </Stack>

              {MEAL_ROWS.map((row, index) => (
                <Box key={row.label}>
                  {index > 0 ? <Divider /> : null}
                  <Stack direction={'row'} alignItems={'center'} justifyContent={'space-around'} my={1.5} mt={index === 0 ? 2 : 1.5}>
                    <Typography component="div" sx={BloodValueStyle}>{targetBlood[row.beforeKey]}
                      <Typography component="div" sx={{ fontSize: 14, color: '#777' }}> mg/dL</Typography>
                    </Typography>

                    <Stack justifyContent={'center'} alignItems={'center'} direction={'column'}>
                      {row.icon}
                      <Typography component="div" sx={dailyTime}>{row.label}</Typography>
                    </Stack>

                    <Typography component="div" sx={BloodValueStyle}>{targetBlood[row.afterKey]}
                      <Typography component="div" sx={{ fontSize: 14, color: '#777' }}> mg/dL</Typography>
                    </Typography>
                  </Stack>
                </Box>
              ))}

              <Stack direction={'row'} justifyContent={'space-evenly'} alignItems={'center'} mt={2} sx={{ bgcolor: '#eee', borderRadius: 3 }}>
                <Typography sx={{ p: 1, fontSize: 14, fontWeight: 700, color: '#555' }}>취침 전</Typography>
              </Stack>

              <Stack direction={'row'} alignItems={'center'} justifyContent={'space-evenly'} my={1.5}>
                <Stack justifyContent={'center'} alignItems={'center'} direction={'row'}>
                  <BedIcon sx={{ color: '#aa1eff', mr: 1 }} />
                  <Typography component="div" sx={dailyTime}>취침 전</Typography>
                </Stack>

                <Typography component="div" sx={{ fontSize: 18, color: '#333', fontWeight: 700, textAlign: 'right', mr: 1 }}>{targetBlood.bloodSugarSleep}
                  <Typography component="span" sx={{ fontSize: 14, color: '#999' }}> mg/dL</Typography>
                </Typography>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* 최근 7일 추이 */}
          <Box sx={{ bgcolor: '#fff', p: '0 10px 10px 10px', borderRadius: '15px', boxShadow: CardShadow }}>
            <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} sx={{ p: 1 }}>
              <Stack>
                <Typography component="div" sx={{ fontSize: 14, color: '#555' }}>최근 7일</Typography>
              </Stack>
              <Stack sx={{ ml: 'auto', zIndex: 2 }}>
                <Button sx={{ fontSize: 12, color: '#555' }} endIcon={<ArrowRightIcon />}
                  onClick={() => { navigate('../history/HistoryDeviceMonthHistory', { state: { deviceId: device.id } }) }}>
                  월간현황
                </Button>
              </Stack>
            </Stack>
            <Divider />

            <Box sx={{ mt: -3.5, mb: 1 }}>
              <Box sx={ChartMarkStyle}>
                <LineChart series={series} xAxis={xAxis} isReferenceLine={false} />
              </Box>
              <BloodLegend />
            </Box>
          </Box>

          <DeviceNormalRange deviceType={device.type} />
        </Box>
      </Grid>

      <Bottom />
    </Stack>
  );
}

// 차트 범례 (시간대별 색상)
export const BloodLegend = () => (
  <Box>
    <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} mt={'-10px'} mb={.5}>
      {BLOOD_TIME_SLOTS.slice(0, 4).map((slot) => (
        <Stack key={slot.key} direction={'row'} justifyContent={'center'} alignItems={'center'} mr={1}>
          <Box sx={{ width: 10, height: 10, bgcolor: slot.color, mr: .5, borderRadius: 3 }}></Box>
          <Typography sx={{ fontSize: 12, color: '#777' }}>{slot.label}</Typography>
        </Stack>
      ))}
    </Stack>
    <Stack direction={'row'} justifyContent={'center'} alignItems={'center'}>
      {BLOOD_TIME_SLOTS.slice(4).map((slot) => (
        <Stack key={slot.key} direction={'row'} justifyContent={'center'} alignItems={'center'} mr={1}>
          <Box sx={{ width: 10, height: 10, bgcolor: slot.color, mr: .5, borderRadius: 3 }}></Box>
          <Typography sx={{ fontSize: 12, color: '#777' }}>{slot.label}</Typography>
        </Stack>
      ))}
    </Stack>
  </Box>
);

const ChartMarkStyle = {
  '& .MuiLineElement-root': { strokeWidth: 0 },
  '& .MuiMarkElement-series-auto-generated-id-0': { strokeWidth: 0, fill: '#800d8d' },
  '& .MuiMarkElement-series-auto-generated-id-1': { strokeWidth: 0, fill: '#bc5ff3b3' },
  '& .MuiMarkElement-series-auto-generated-id-2': { strokeWidth: 0, fill: '#2c6914' },
  '& .MuiMarkElement-series-auto-generated-id-3': { strokeWidth: 0, fill: '#77c059' },
  '& .MuiMarkElement-series-auto-generated-id-4': { strokeWidth: 0, fill: '#d9448d' },
  '& .MuiMarkElement-series-auto-generated-id-5': { strokeWidth: 0, fill: '#ec70ac' },
  '& .MuiMarkElement-series-auto-generated-id-6': { strokeWidth: 0, fill: '#eac222' },
};

const BloodValueStyle = {
  fontSize: 18, color: '#333', fontWeight: 700, textAlign: 'center', mr: 1
};

const dailyTime = {
  width: 45, textAlign: 'center', fontSize: 14, color: '#777', fontWeight: 700
};

const CardShadow = '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)';

export default HistoryDeviceBloodDetail;
